/**
 * Six big boxes for the email sign-in code (docs/AUTH.md), the same pattern as CodeBoxes: the numeric keyboard, the
 * cursor moves on, Backspace moves back, and pasting (or the phone's "from Messages/Mail" autofill) fills all six.
 */
import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

export const DIGITS = 6;
const onlyDigits = (s: string) => s.replace(/\D/g, "");

export function DigitBoxes({ value, onChange, disabled }: { value: string; onChange: (code: string) => void; disabled?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  // Empty boxes are spaces in `value`, so a digit keeps its place when an earlier box is cleared.
  const chars = Array.from({ length: DIGITS }, (_, i) => (value[i] ?? "").trim());
  const emit = (next: string[]) => onChange(next.map((x) => x || " ").join("").trimEnd());

  /** Several digits at once (paste or autofill) fill from box `from` on. */
  function spread(from: number, digits: string) {
    const next = [...chars];
    const take = digits.slice(0, DIGITS - from);
    [...take].forEach((d, k) => { next[from + k] = d; });
    emit(next);
    refs.current[Math.min(from + take.length, DIGITS - 1)]?.focus();
  }
  function put(i: number, typed: string) {
    const d = onlyDigits(typed);
    if (!d) { emit(chars.map((x, j) => (j === i ? "" : x))); return; }
    if (d.length > 2 || (d.length === 2 && !chars[i])) { spread(i, d); return; }
    emit(chars.map((x, j) => (j === i ? d.slice(-1) : x)));
    refs.current[Math.min(i + 1, DIGITS - 1)]?.focus();
  }
  function paste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    const d = onlyDigits(e.clipboardData.getData("text"));
    if (!d) return;
    e.preventDefault();
    // A whole code always fills from the first box, wherever it was pasted.
    spread(d.length >= DIGITS ? 0 : i, d);
  }
  function key(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !chars[i] && i > 0) refs.current[i - 1]?.focus();
  }

  return (
    <div className="code-boxes" role="group" aria-label="Your 6-digit code">
      {chars.map((c, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }} className="code-box" value={c} aria-label={`Digit ${i + 1} of ${DIGITS}`}
          inputMode="numeric" pattern="[0-9]*" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={i === 0 ? DIGITS : 2}
          disabled={disabled} autoFocus={i === 0} onChange={(e) => put(i, e.target.value)} onPaste={(e) => paste(i, e)}
          onKeyDown={(e) => key(i, e)} onFocus={(e) => e.target.select()} />
      ))}
    </div>
  );
}
